import { Link } from "react-router-dom";
import { useBooked, useOverview } from "@/api/books";
import {
  entriesHref,
  fixHref,
  pendingWords,
  recordHref,
  type SourceRef,
} from "@/components/books/links";
import { cn } from "@/lib/utils";

/** A record's name, linking to the page it lives on; plain text when it has none. */
export function RecordLink({
  source,
  label,
  className,
}: {
  source: SourceRef;
  label: string;
  className?: string;
}) {
  const href = recordHref(source);
  if (!href) return <span className={cn("text-muted-foreground", className)}>{label}</span>;
  return (
    <Link to={href} className={cn("text-primary hover:underline", className)}>
      {label}
    </Link>
  );
}

type BooksLinkProps = {
  /** The record's kind as the books name it: "expense", "invoice", "credit_note"... */
  kind: string;
  id: string;
};

/** Where one record stands in the books: its entry, or why it is not there yet. Every row
 *  of a list shares the same two requests. Renders nothing until both have answered (or if
 *  either fails: the Books page itself shows that error). */
export function BooksLink({ kind, id }: BooksLinkProps) {
  const booked = useBooked(kind);
  const overview = useOverview();
  if (!booked.data || !overview.data) return null;
  const entry = booked.data.find((row) => row.source_id === id);
  // A bill's payment and an invoice's write-off share the record's id but are entries of
  // their own: they are named, and do not stand for the record itself.
  const waiting = overview.data.pending.filter((row) => row.source_id === id);
  const itselfWaits = waiting.some((row) => row.source_kind === kind);

  return (
    <span className="flex flex-wrap gap-x-3 text-xs">
      {entry && (
        <Link to={entriesHref(kind, id)} className="text-primary hover:underline">
          In the books · {entry.label}
        </Link>
      )}
      {waiting.map((row) => (
        <Link
          key={row.source_kind}
          to={fixHref(row) ?? "/books"}
          className="text-foreground underline decoration-warning decoration-2 underline-offset-2"
        >
          {row.source_kind === kind ? "Not in the books yet" : `${row.label} not in the books yet`}:{" "}
          {pendingWords(row)}
        </Link>
      ))}
      {!entry && !itselfWaits && (
        <Link to="/books" className="text-muted-foreground hover:underline">
          Not posted yet · goes in at the next posting
        </Link>
      )}
    </span>
  );
}
